import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import MagneticButton from "./MagneticButton";

const LINKS = [
  { label: "What We Build", href: "/#services" },
  { label: "Pricing", href: "/#pricing" },
  { label: "Process", href: "/#process" },
  { label: "Stack", href: "/#tech-stack" },
  { label: "About", href: "/about" },
  { label: "Book Call", href: "/book-call" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-4 left-0 right-0 z-50 transition-all duration-500 flex justify-center px-4`}
    >
      <div className={`max-w-5xl w-full mx-auto px-6 py-3 flex items-center justify-between rounded-full transition-all duration-500 ${
        scrolled
          ? "bg-ink-50/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/50"
          : "bg-transparent border border-transparent"
      }`}>
        <a
          href="/"
          data-testid="navbar-logo"
          className="flex items-center gap-2.5 group"
        >
          <div className="w-9 h-9 flex items-center justify-center overflow-hidden">
            <img src="/logo.svg" alt="Devzcorner logo" className="w-full h-full object-contain" />
          </div>
          <span className="font-display text-xl tracking-tight">
            Devzcorner
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s|&/g, "")}`}
              className="text-sm text-zinc-400 hover:text-white transition-colors link-underline"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <MagneticButton>
            <a
              href="/#cta"
              data-testid="navbar-cta-start-project"
              className="hidden md:inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white text-black text-sm font-semibold hover:bg-zinc-200 transition-all hover:scale-[1.03] shadow-[0_0_20px_-5px_rgba(255,255,255,0.4)]"
            >
              Start a Project
              <ArrowUpRight size={16} strokeWidth={2.5} />
            </a>
          </MagneticButton>
            <button
              aria-label="Toggle menu"
              data-testid="navbar-mobile-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden w-10 h-10 inline-flex items-center justify-center border border-white/10 rounded-full hover:bg-white/5 transition-colors"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-24 left-4 right-4 z-40 lg:hidden bg-ink-50/95 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden"
            data-testid="navbar-mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-lg font-medium text-zinc-300 hover:text-white"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="/#cta"
                onClick={() => setOpen(false)}
                className="mt-4 inline-flex items-center justify-center px-6 py-4 rounded-full bg-white text-black text-base font-semibold"
              >
                Start a Project
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
